// src/features/executive/insight/widgets/AvgVisitsTile.tsx
import { Box, Chip, Stack, Tooltip, Typography } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { useTranslation } from 'react-i18next'
import KPICard from 'features/executive/components/KPICard/KPICard'
import EmptyState from 'features/executive/components/EmptyState/EmptyState'
import ErrorState from 'features/executive/components/ErrorState/ErrorState'
import LoadingSkeleton from 'features/executive/components/LoadingSkeleton/LoadingSkeleton'
import { formatVariance } from 'utils/executive.utils'
import { useAvgVisits } from '../hooks/use-avg-visits'

// Same threshold as #12 RetentionRateTile — < 30 patients = unstable
// proportion estimates, where a single power user can move the mean by 1+
// visit-days. Flag the caveat so execs don't over-interpret.
const SMALL_COHORT_THRESHOLD = 30

// Skew threshold — mean noticeably above median means a few power users
// are pulling the average up. 1.5x is a reasonable rule of thumb in
// healthcare-engagement metrics.
const SKEW_RATIO = 1.5

// Absolute-level thresholds calibrated to TDH-style dental practice:
// • ≥ 2.0 visits/patient = multi-stage TX completion is the norm
// • < 1.3 = most patients come once and don't return inside the window
const LEVEL_HIGH = 2.0
const LEVEL_LOW = 1.3

// Relative variance threshold for shift chip — engagement frequency is
// noisier than retention rate, so use a slightly higher bar than #12.
const SHIFT_MIN_RATIO = 0.15

const fmt = (n: number) => new Intl.NumberFormat('th-TH').format(Math.round(n || 0))

function varianceOf(current: number, previous: number | null | undefined): number | null {
  if (previous == null || previous === 0) return null
  return (current - previous) / Math.abs(previous)
}

export default function AvgVisitsTile() {
  const { t } = useTranslation()
  const { data, loading, error, refetch } = useAvgVisits()

  if (loading) return <LoadingSkeleton height={160} />
  if (error) return <ErrorState onRetry={refetch} />
  if (!data || data.totalPatients === 0) {
    return (
      <EmptyState
        message={t(
          'EXECUTIVE.INSIGHT.AVG_VISITS.EMPTY',
          'ไม่พบคนไข้ในช่วงเวลาและเงื่อนไขที่เลือก'
        )}
      />
    )
  }

  const avg = data.avgVisits
  const med = data.medianVisits ?? null
  const previousAvg = data.previousAvg
  const previousMed = data.previousMedianVisits ?? null
  const previousTotalVisits = data.previousTotalVisits ?? null
  const previousTotalPatients = data.previousTotalPatients ?? null

  const variance = varianceOf(avg, previousAvg)
  const smallCohort = data.totalPatients < SMALL_COHORT_THRESHOLD
  const skewed = med != null && med > 0 && avg > med * SKEW_RATIO

  const showHigh = avg >= LEVEL_HIGH
  const showLow = avg < LEVEL_LOW && data.totalPatients > 0
  const showShiftUp = variance != null && variance >= SHIFT_MIN_RATIO
  const showShiftDown = variance != null && variance <= -SHIFT_MIN_RATIO

  const tooltipIcon = (
    <Tooltip
      title={t(
        'EXECUTIVE.INSIGHT.AVG_VISITS.SOURCE_TOOLTIP',
        '"Visit" = 1 วันที่ใดๆ ที่คนไข้มี orders (orders ในวันเดียวกันนับเป็น 1 visit แม้มีหลาย orders)\n\nAvg Visits = totalVisits ÷ totalPatients (arithmetic mean)\n\nวิธีอ่าน:\n• ≥ 2.0 = engagement สูง (TX ส่วนใหญ่จบใน 2+ visits)\n• 1.3–2.0 = ปกติ\n• < 1.3 = ส่วนใหญ่มาครั้งเดียว — ไม่มี TX ต่อเนื่อง\n\nต่างจาก #12 Retention Rate:\n• #12 = "% คนไข้ที่กลับมา" (binary ≥2 visits)\n• #13 = "เฉลี่ยกลับมาบ่อยแค่ไหน" (continuous)\n\nMedian โผล่ขึ้นเมื่อ skew — mean สูงกว่า median × 1.5 = power-users ดันค่าเฉลี่ย'
      )}
      arrow
      placement="top"
      componentsProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 400 } } }}
    >
      <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.disabled', cursor: 'help' }} />
    </Tooltip>
  )

  // Render median inline next to mean — small dimmed pill that visually
  // anchors the skew warning when present.
  const valueAdornment = med != null && med > 0 ? (
    <Tooltip
      title={t(
        'EXECUTIVE.INSIGHT.AVG_VISITS.MEDIAN_TOOLTIP',
        'มัธยฐาน = ค่ากลางของชุดข้อมูล — ทนทานต่อค่าที่สูง/ต่ำผิดปกติได้มากกว่าค่าเฉลี่ย'
      )}
      arrow
      placement="top"
    >
      <Typography
        variant="caption"
        sx={{
          color: 'text.disabled',
          cursor: 'help',
          fontWeight: 500,
          ml: 0.5,
        }}
      >
        {t('EXECUTIVE.INSIGHT.AVG_VISITS.MEDIAN_INLINE', 'มัธยฐาน {{m}}', { m: med.toFixed(1) })}
      </Typography>
    </Tooltip>
  ) : undefined

  return (
    <KPICard
      title={t('EXECUTIVE.INSIGHT.AVG_VISITS.TITLE')}
      value={avg.toFixed(1)}
      headerAction={tooltipIcon}
      valueAdornment={valueAdornment}
      caption={
        <Box>
          {/* Current window raw counts */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {t('EXECUTIVE.INSIGHT.AVG_VISITS.CAPTION', {
              visits: fmt(data.totalVisits),
              patients: fmt(data.totalPatients),
            })}
          </Typography>

          {/* Prev period raw counts + avg + median for comparison */}
          {previousAvg != null && previousTotalVisits != null && previousTotalPatients != null && (
            <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>
              {t(
                'EXECUTIVE.INSIGHT.AVG_VISITS.PREV_CAPTION',
                'ก่อนหน้า: {{visits}}/{{patients}} (เฉลี่ย {{avg}}{{med}})',
                {
                  visits: fmt(previousTotalVisits),
                  patients: fmt(previousTotalPatients),
                  avg: previousAvg.toFixed(1),
                  med: previousMed != null ? `, มัธยฐาน ${previousMed.toFixed(1)}` : '',
                }
              )}
            </Typography>
          )}

          {/* Chips: small-cohort + skew (data-quality) + level + shift (interpretation) */}
          <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', gap: 0.5 }}>
            {smallCohort && (
              <Chip
                icon={<WarningAmberOutlinedIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.AVG_VISITS.SMALL_COHORT',
                      'กลุ่มตัวอย่างน้อย ({{count}} คน) — ค่าเฉลี่ยอาจยังไม่เสถียร',
                      { count: fmt(data.totalPatients) } as any
                    )}
                  </Typography>
                }
                color="warning"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
            {skewed && med != null && (
              <Chip
                icon={<InfoOutlinedIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.AVG_VISITS.SKEW',
                      'ค่าเฉลี่ย ({{mean}}) สูงกว่าค่ามัธยฐาน ({{median}}) — มีคนไข้บางกลุ่มมาบ่อยมาก ดึงค่าเฉลี่ยขึ้น',
                      { mean: avg.toFixed(1), median: med.toFixed(1) }
                    )}
                  </Typography>
                }
                color="info"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
            {showHigh && (
              <Chip
                icon={<CheckCircleOutlineIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.AVG_VISITS.LEVEL_HIGH',
                      'ความถี่สูง: เฉลี่ย {{avg}} ครั้ง/คน',
                      { avg: avg.toFixed(1) }
                    )}
                  </Typography>
                }
                color="success"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
            {showLow && (
              <Chip
                icon={<WarningAmberOutlinedIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.AVG_VISITS.LEVEL_LOW',
                      'ความถี่ต่ำ: เฉลี่ย {{avg}} ครั้ง/คน — ส่วนใหญ่มาครั้งเดียว',
                      { avg: avg.toFixed(1) }
                    )}
                  </Typography>
                }
                color="warning"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
            {showShiftUp && variance != null && (
              <Chip
                icon={<TrendingUpIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.AVG_VISITS.SHIFT_UP',
                      'ความถี่เพิ่มขึ้น {{pct}}',
                      { pct: formatVariance(variance) }
                    )}
                  </Typography>
                }
                color="info"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
            {showShiftDown && variance != null && (
              <Chip
                icon={<TrendingDownIcon />}
                label={
                  <Typography variant="caption" sx={{ whiteSpace: 'normal' }}>
                    {t(
                      'EXECUTIVE.INSIGHT.AVG_VISITS.SHIFT_DOWN',
                      'ความถี่ลดลง {{pct}}',
                      { pct: formatVariance(variance) }
                    )}
                  </Typography>
                }
                color="warning"
                variant="outlined"
                size="small"
                sx={{ height: 'auto', '& .MuiChip-label': { px: 1, py: 0.25 } }}
              />
            )}
          </Stack>
        </Box>
      }
    />
  )
}
